import React from "react";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { ProductComparisonTool } from "@/components/ProductComparisonTool";
import { Badge } from "@/components/ui/badge";
import { Scale, CheckCircle } from "lucide-react";

export default function Compare() {
  const { t, language } = useI18n();
  const seo = getSeoCopy("compare", language);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead route="compare" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <Badge variant="outline" className="border-accent/30 text-accent bg-accent/10 mb-6 text-lg px-4 py-2">
              Product Comparison
            </Badge>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl font-light mb-6 tracking-tight text-primary">
              {seo.h1}
            </h1>
            <p className="font-inter text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
              Make informed B2B purchasing decisions with our interactive comparison tool. 
              Compare features, specifications, and benefits across our entire smart toilet collection.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Compare up to 4 products</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Export to CSV</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Detailed specifications</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-12">
        <BreadcrumbNav items={[
          { label: "Home", href: "/" },
          { label: "Smart Toilets", href: "/smart-toilets" },
          { label: "Compare" }
        ]} />

        {/* Comparison Tool */}
        <div className="mt-8">
          <ProductComparisonTool />
        </div>

        {/* Benefits Section */}
        <section className="mt-16 py-12 bg-muted/30 rounded-2xl px-8">
          <h2 className="text-2xl font-bold text-center mb-8">Why Use Our Comparison Tool?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scale className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Side-by-Side Comparison</h3>
              <p className="text-sm text-muted-foreground">
                View all features and specifications in an easy-to-read table format for quick decision making.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">B2B Focused</h3>
              <p className="text-sm text-muted-foreground">
                Designed for distributors, architects, and developers to make informed bulk purchasing decisions.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scale className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Export & Share</h3>
              <p className="text-sm text-muted-foreground">
                Download comparison data as CSV for presentations, proposals, and internal team discussions.
              </p>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
